import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, FolderOpen, Monitor, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface AppShellProps {
  children: ReactNode;
  onSignOut: () => Promise<void>;
  userName?: string | null;
}

const navItems = [
  {
    label: 'Repositories',
    path: '/repos',
    icon: FolderOpen
  },
  {
    label: 'Devices',
    path: '/devices',
    icon: Monitor
  },
  {
    label: 'Configuration',
    path: '/configuration',
    icon: Settings
  }
];

export const AppShell = ({ children, onSignOut, userName }: AppShellProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <div className="relative flex min-h-screen flex-col lg:flex-row">
        <aside className="relative z-10 w-full border-b border-border bg-card/80 px-6 py-6 backdrop-blur lg:min-h-screen lg:w-64 lg:border-b-0 lg:border-r">
          <div className="flex items-center justify-between lg:flex-col lg:items-start lg:gap-6">
            <div className="space-y-1">
              <h1 className="text-xl font-semibold">Filesync</h1>
              <p className="text-xs text-muted-foreground">Repository control plane</p>
            </div>
            <div className="hidden text-sm text-muted-foreground lg:block">
              {userName ? `Signed in as ${userName}` : 'Signed in'}
            </div>
          </div>
          <Separator className="my-6" />
          <nav className="flex gap-2 lg:flex-col">
            {navItems.map(item => {
              const isActive = location.pathname.startsWith(item.path);
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? 'secondary' : 'ghost'}
                  className={cn('w-full justify-start gap-2', isActive && 'text-foreground')}
                  onClick={() => navigate(item.path)}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Button>
              );
            })}
          </nav>
          <div className="mt-6 hidden lg:block">
            <Button
              variant="outline"
              className="w-full justify-center"
              onClick={() => {
                void onSignOut();
              }}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </aside>

        <div className="relative flex-1 overflow-hidden">
          <div className="pointer-events-none absolute right-0 top-0 h-64 w-64 -translate-y-1/3 translate-x-1/3 rounded-full bg-primary/15 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 translate-y-1/3 rounded-full bg-secondary/20 blur-3xl" />

          <header className="relative z-10 flex items-center justify-end gap-4 border-b border-border bg-background/70 px-6 py-4 backdrop-blur">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => {
                void onSignOut();
              }}
              aria-label={userName ? `Sign out ${userName}` : 'Sign out'}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </header>

          <main className="relative z-10 px-6 py-8">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};
